import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { Aparece, EASE_SALIDA, useSinAnimacion } from "./Aparece";
import { PROMESA } from "./content";
import { Proximamente } from "./Proximamente";

// "Tu cartera crece. Tu tiempo se mantiene." con un gráfico CONCEPTUAL: dos líneas sin cifras,
// rotulado "ilustrativo". No hay datos de clientes todavía, así que el gráfico muestra la forma de
// la promesa y no la mide.
//
// Colores validados con el script de la skill dataviz (#4F46E5 + #D97706: los 6 chequeos en verde,
// ΔE 34 en protanopia). Identidad nunca sólo por color: leyenda + etiqueta directa en cada línea +
// trazo distinto (continuo / discontinuo). Sin tooltip: no hay valores que mostrar.

const PROPIEDADES = "#4F46E5";
const HORAS = "#D97706";

const Leyenda = ({ color, discontinua, children }) => (
  <li className="flex items-center gap-2 text-xs font-semibold text-slate-600">
    <svg width="22" height="6" aria-hidden="true">
      <line x1="1" y1="3" x2="21" y2="3" stroke={color} strokeWidth="2" strokeLinecap="round" strokeDasharray={discontinua ? "4 4" : undefined} />
    </svg>
    {children}
  </li>
);

const GraficoConceptual = () => {
  const sinAnimacion = useSinAnimacion();
  const dibujo = (delay) =>
    sinAnimacion
      ? {}
      : {
          initial: { pathLength: 0 },
          whileInView: { pathLength: 1 },
          viewport: { once: true, amount: 0.4 },
          transition: { duration: 1.1, ease: EASE_SALIDA, delay },
        };
  return (
    <figure className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-inset ring-slate-200/60">
      <figcaption className="flex items-start justify-between gap-4">
        <p className="text-sm font-bold text-slate-800">Con SEBBIA, a medida que crece tu cartera</p>
        <span className="shrink-0 rounded-full bg-slate-500/10 px-2.5 py-1 text-[11px] font-semibold text-slate-600">Ilustrativo</span>
      </figcaption>
      <ul className="mt-4 flex flex-wrap gap-x-5 gap-y-2">
        <Leyenda color={PROPIEDADES}>Propiedades administradas</Leyenda>
        <Leyenda color={HORAS} discontinua>Horas de gestión</Leyenda>
      </ul>
      <svg
        viewBox="0 0 400 200"
        className="mt-4 h-auto w-full"
        role="img"
        aria-label="Gráfico ilustrativo: las propiedades administradas suben mientras las horas de gestión se mantienen planas."
      >
        {/* Grilla recesiva: tres líneas horizontales */}
        {[40, 90, 140].map((y) => (
          <line key={y} x1="0" x2="400" y1={y} y2={y} stroke="#e2e8f0" strokeWidth="1" />
        ))}
        <line x1="0" x2="400" y1="180" y2="180" stroke="#cbd5e1" strokeWidth="1" />
        <motion.path
          d="M8 160 C 90 150, 150 120, 220 92 S 330 40, 392 28"
          fill="none"
          stroke={PROPIEDADES}
          strokeWidth="2"
          strokeLinecap="round"
          {...dibujo(0.1)}
        />
        <motion.path
          d="M8 132 C 120 130, 260 134, 392 131"
          fill="none"
          stroke={HORAS}
          strokeWidth="2"
          strokeLinecap="round"
          strokeDasharray="6 6"
          {...dibujo(0.35)}
        />
        <circle cx="392" cy="28" r="4" fill={PROPIEDADES} stroke="#fff" strokeWidth="2" />
        <circle cx="392" cy="131" r="4" fill={HORAS} stroke="#fff" strokeWidth="2" />
        {/* Etiquetas directas en tinta de texto, no en el color de la serie */}
        <text x="384" y="18" textAnchor="end" className="fill-slate-700 text-[11px] font-semibold">Más propiedades</text>
        <text x="384" y="121" textAnchor="end" className="fill-slate-700 text-[11px] font-semibold">El mismo tiempo</text>
        <text x="8" y="197" className="fill-slate-400 text-[10px] font-semibold uppercase tracking-widest">Hoy</text>
        <text x="392" y="197" textAnchor="end" className="fill-slate-400 text-[10px] font-semibold uppercase tracking-widest">Tu cartera en un año</text>
      </svg>
    </figure>
  );
};

export const Promesa = () => (
  <section className="bg-white" data-testid="landing-promesa">
    <div className="mx-auto grid max-w-6xl items-center gap-12 px-4 py-20 sm:px-6 lg:grid-cols-[1fr_1fr] lg:gap-16 lg:py-28">
      <Aparece>
        <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">{PROMESA.eyebrow}</p>
        <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">
          {PROMESA.titulo} <span className="block text-slate-400">{PROMESA.tituloTenue}</span>
        </h2>
        <p className="mt-6 border-l-4 border-l-indigo-500 pl-4 text-lg font-semibold leading-relaxed text-slate-800">
          {PROMESA.bajada}
        </p>
        <p className="mt-5 max-w-xl text-base leading-relaxed text-slate-600">{PROMESA.texto}</p>
        <ul className="mt-6 space-y-3">
          {PROMESA.puntos.map((p) => {
            // Un punto es texto, o `{ texto, proximamente }` si describe algo que todavía no existe.
            const { texto, proximamente } = typeof p === "string" ? { texto: p } : p;
            return (
              <li key={texto} className="flex gap-2.5 text-sm leading-relaxed text-slate-600">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
                <span>
                  {texto}
                  {proximamente && <Proximamente className="ml-2 align-middle" />}
                </span>
              </li>
            );
          })}
        </ul>
      </Aparece>
      <Aparece delay={0.1}>
        <GraficoConceptual />
      </Aparece>
    </div>
  </section>
);
